import { useSyncExternalStore } from "react";

import type { ClientId, CoClient, CoProgress } from "../domain/types";
import { FIRST_CO_SLUG } from "../domain/steps";
import type { OnboardingClientDTO } from "./onboardingApi";
import * as api from "./onboardingApi";

/**
 * Onboarding state, held in memory for the current screen and persisted to the
 * onboarding_* tables through the API.
 *
 * Writes are applied to the in-memory copy immediately so typing stays
 * responsive, then flushed to the server debounced. Continue awaits
 * flushPending() before navigating, so a reload never loses the last
 * keystroke. Reads come from the aggregate the server returns, which is the
 * single source of truth on load.
 */

const FLUSH_DELAY_MS = 600;

interface Entry {
  client: CoClient;
  progress: CoProgress;
  answers: Record<string, unknown>;
}

const entries = new Map<ClientId, Entry>();
const loading = new Set<ClientId>();
const loadErrors = new Map<ClientId, string>();

// Field-level patches waiting to go to the server, per client.
const pendingPatches = new Map<ClientId, Record<string, unknown>>();
const pendingServices = new Map<ClientId, string[]>();
// Clients with an edit that has not round-tripped yet — queued above, or
// currently in flight. While a client is here, absorb() keeps the local
// client object instead of the server snapshot's: answer and progress saves
// respond immediately with an aggregate built BEFORE the debounced patch
// lands, and taking their client fields snapped the UI back to the pre-edit
// value for ~600ms (visible as the bookkeeping-type tick flicking
// new → old → new on Books questions).
const dirtyClients = new Set<ClientId>();
let flushTimer: ReturnType<typeof setTimeout> | undefined;
let inFlight: Promise<void> = Promise.resolve();

const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// ── DTO ↔ domain ────────────────────────────────────────────────────────────

function toClient(dto: OnboardingClientDTO): CoClient {
  return {
    id: dto.public_id,
    name: dto.name ?? "",
    entityType: (dto.entity_type as CoClient["entityType"]) ?? "private-limited",
    services: (dto.services as CoClient["services"]) ?? [],
    backend: {
      system: (dto.backend_system as CoClient["backend"]["system"]) ?? "zoho",
      status: (dto.backend_status as CoClient["backend"]["status"]) ?? "active",
    },
    intakeMode: (dto.intake_mode as CoClient["intakeMode"]) ?? "fetch",
    accountStartDate: dto.account_start_date ?? "",
    tallyCompanyName: dto.tally_company_name ?? "",
    tallyGstin: dto.tally_gstin ?? "",
    tallyGstState: dto.tally_gst_state ?? "",
    tallyGstRegistrationType: dto.tally_gst_registration_type ?? "",
    subdomain: dto.subdomain ?? "",
    tenantDomain: dto.tenant_domain ?? undefined,
    companyId: dto.company_id ?? null,
    cfoSource: (dto.cfo_source as CoClient["cfoSource"]) ?? undefined,
    businessNature: dto.business_nature_category
      ? {
          category: dto.business_nature_category as NonNullable<CoClient["businessNature"]>["category"],
          description: dto.business_nature_description ?? "",
        }
      : undefined,
    bookkeepingType: (dto.bookkeeping_type as CoClient["bookkeepingType"]) ?? undefined,
    zohoSizing: dto.zoho_sizing
      ? {
          docsPerYear: dto.zoho_sizing.docs_per_year ?? undefined,
          userCount: dto.zoho_sizing.user_count,
          multiCurrency: dto.zoho_sizing.multi_currency ?? undefined,
          gstinCount: dto.zoho_sizing.gstin_count,
          recommendedPlan: dto.zoho_sizing.recommended_plan ?? undefined,
          chosenPlan: dto.zoho_sizing.chosen_plan ?? undefined,
          drivingRules: dto.zoho_sizing.driving_rules,
          needsZohoCall: dto.zoho_sizing.needs_zoho_call,
        }
      : undefined,
    status: dto.status,
    liveAt: dto.live_at ?? undefined,
    createdAt: dto.created_at ?? "",
    updatedAt: dto.updated_at ?? "",
  };
}

// The one place domain field names become column names.
const PATCH_FIELD_MAP: Record<string, string> = {
  name: "name",
  entityType: "entity_type",
  subdomain: "subdomain",
  intakeMode: "intake_mode",
  bookkeepingType: "bookkeeping_type",
  cfoSource: "cfo_source",
  accountStartDate: "account_start_date",
  tallyGstin: "tally_gstin",
  tallyGstState: "tally_gst_state",
  tallyGstRegistrationType: "tally_gst_registration_type",
};

function toServerPatch(patch: Partial<CoClient>): Record<string, unknown> {
  const out: Record<string, unknown> = {};

  Object.keys(PATCH_FIELD_MAP).forEach((key) => {
    if (key in patch) {
      out[PATCH_FIELD_MAP[key]] = (patch as Record<string, unknown>)[key] ?? null;
    }
  });

  if (patch.businessNature !== undefined) {
    out.business_nature_category = patch.businessNature ? patch.businessNature.category : null;
    out.business_nature_description = patch.businessNature ? patch.businessNature.description : null;
  }

  if (patch.backend !== undefined) {
    out.backend_system = patch.backend.system;
    out.backend_status = patch.backend.status;
  }

  return out;
}

function absorb(dto: OnboardingClientDTO) {
  const local = entries.get(dto.public_id);
  entries.set(dto.public_id, {
    client: local !== undefined && dirtyClients.has(dto.public_id) ? local.client : toClient(dto),
    progress: { currentSlug: dto.current_step || FIRST_CO_SLUG, doneSlugs: dto.done_slugs ?? [] },
    answers: dto.answers ?? {},
  });
  emit();
}

// ── loading ─────────────────────────────────────────────────────────────────

/**
 * Load a client's aggregate. Safe to call from render effects — concurrent
 * calls for the same id collapse into one request.
 */
export function ensureLoaded(clientId: ClientId): void {
  if (entries.has(clientId) || loading.has(clientId)) return;

  loading.add(clientId);
  loadErrors.delete(clientId);
  // Publish the loading flag before the request returns. Without this the
  // step page's first paint has no client and no loading bit, so a Tally
  // draft renders the Zoho customers step and shows 0 until a full reload.
  emit();

  api
    .fetchDraft(clientId)
    .then(absorb)
    .catch((error: Error) => {
      loadErrors.set(clientId, error.message);
      emit();
    })
    .finally(() => {
      loading.delete(clientId);
      emit();
    });
}

export function isLoading(clientId: ClientId): boolean {
  return loading.has(clientId) && !entries.has(clientId);
}

export function loadError(clientId: ClientId): string | undefined {
  return loadErrors.get(clientId);
}

// ── writes ──────────────────────────────────────────────────────────────────

function scheduleFlush() {
  if (flushTimer !== undefined) {
    clearTimeout(flushTimer);
  }
  flushTimer = setTimeout(() => {
    void flushPending();
  }, FLUSH_DELAY_MS);
}

/**
 * Push everything queued to the server. Awaited by Continue so navigation
 * never outruns persistence. Serialised through `inFlight` so two rapid
 * flushes can't race on the same row.
 */
export function flushPending(): Promise<void> {
  if (flushTimer !== undefined) {
    clearTimeout(flushTimer);
    flushTimer = undefined;
  }

  const patches = new Map(pendingPatches);
  const services = new Map(pendingServices);
  pendingPatches.clear();
  pendingServices.clear();

  if (patches.size === 0 && services.size === 0) {
    return inFlight;
  }

  const patchList: [ClientId, Record<string, unknown>][] = [];
  patches.forEach((patch, clientId) => patchList.push([clientId, patch]));
  const serviceList: [ClientId, string[]][] = [];
  services.forEach((codes, clientId) => serviceList.push([clientId, codes]));

  // A client stops being dirty only when the LAST of its writes in this
  // flush returns and nothing new was queued while it was in flight. On
  // failure it stays dirty — the optimistic value stands, so stale
  // snapshots must keep losing to it.
  const stillHasWork = (clientId: ClientId, servicesAhead: boolean) =>
    pendingPatches.has(clientId) || pendingServices.has(clientId) || servicesAhead;
  const serviceClients = new Set(serviceList.map(([clientId]) => clientId));

  inFlight = inFlight.then(async () => {
    for (let i = 0; i < patchList.length; i++) {
      try {
        const dto = await api.patchDraft(patchList[i][0], patchList[i][1]);
        if (!stillHasWork(patchList[i][0], serviceClients.has(patchList[i][0]))) {
          dirtyClients.delete(patchList[i][0]);
        }
        absorb(dto);
      } catch {
        // Keep the optimistic value on screen; the next write retries.
      }
    }
    for (let i = 0; i < serviceList.length; i++) {
      try {
        const dto = await api.putServices(serviceList[i][0], serviceList[i][1]);
        if (!stillHasWork(serviceList[i][0], false)) {
          dirtyClients.delete(serviceList[i][0]);
        }
        absorb(dto);
      } catch {
        // Same: the in-memory selection stands until the next attempt.
      }
    }
  });

  return inFlight;
}

export function getClient(clientId: ClientId): CoClient | undefined {
  const entry = entries.get(clientId);
  return entry ? entry.client : undefined;
}

/**
 * Apply a patch locally and queue it. `services` is queued separately because
 * it is a set-replace on its own table, not a column update.
 */
export function updateClient(clientId: ClientId, patch: Partial<CoClient>): void {
  const entry = entries.get(clientId);
  if (!entry) return;

  entries.set(clientId, { ...entry, client: { ...entry.client, ...patch } });
  emit();

  if (patch.services !== undefined) {
    pendingServices.set(clientId, patch.services);
    dirtyClients.add(clientId);
  }

  const serverPatch = toServerPatch(patch);
  if (Object.keys(serverPatch).length > 0) {
    pendingPatches.set(clientId, { ...(pendingPatches.get(clientId) ?? {}), ...serverPatch });
    dirtyClients.add(clientId);
  }

  scheduleFlush();
}

// Stable default so useSyncExternalStore sees the same snapshot until the
// entry actually loads — a fresh object per call loops the render.
const DEFAULT_PROGRESS: CoProgress = { currentSlug: FIRST_CO_SLUG, doneSlugs: [] };

export function getProgress(clientId: ClientId): CoProgress {
  const entry = entries.get(clientId);
  return entry ? entry.progress : DEFAULT_PROGRESS;
}

/**
 * Record that the user is looking at `slug`. Fires and forgets — landing on a
 * step is not worth blocking the render on.
 */
export function setCurrentSlug(clientId: ClientId, slug: string): void {
  const entry = entries.get(clientId);
  if (!entry || entry.progress.currentSlug === slug) return;

  entries.set(clientId, { ...entry, progress: { ...entry.progress, currentSlug: slug } });
  emit();

  void api.saveProgress(clientId, slug, "in_progress").then(absorb).catch(() => undefined);
}

/**
 * Mark a step done and move current_step to `nextSlug`, so a resume lands on
 * the step the user was heading to rather than the one they just finished.
 */
export async function markDone(clientId: ClientId, slug: string, nextSlug?: string): Promise<void> {
  const entry = entries.get(clientId);
  if (entry && !entry.progress.doneSlugs.includes(slug)) {
    entries.set(clientId, {
      ...entry,
      progress: {
        currentSlug: nextSlug ?? entry.progress.currentSlug,
        doneSlugs: [...entry.progress.doneSlugs, slug],
      },
    });
    emit();
  }

  try {
    absorb(await api.saveProgress(clientId, slug, "done", nextSlug));
  } catch {
    // The optimistic state stands; the next navigation re-sends.
  }
}

// ── answers (the key/value long tail) ───────────────────────────────────────

export function getAnswer<T>(clientId: ClientId, key: string): T | undefined {
  const entry = entries.get(clientId);
  return entry ? (entry.answers[key] as T | undefined) : undefined;
}

export async function persistAnswer<T>(clientId: ClientId, key: string, value: T): Promise<void> {
  const entry = entries.get(clientId);
  if (entry) {
    entries.set(clientId, { ...entry, answers: { ...entry.answers, [key]: value } });
    emit();
  }

  try {
    absorb(await api.saveAnswer(clientId, key, value));
  } catch {
    // Optimistic value stands.
  }
}

export async function persistSizing(
  clientId: ClientId,
  sizing: Record<string, unknown>
): Promise<void> {
  try {
    absorb(await api.saveSizing(clientId, sizing));
  } catch {
    // Optimistic value stands.
  }
}

// ── hooks ───────────────────────────────────────────────────────────────────

export function useCoClient(clientId: ClientId): CoClient | undefined {
  return useSyncExternalStore(subscribe, () => {
    const entry = entries.get(clientId);
    return entry ? entry.client : undefined;
  });
}

export function useCoProgress(clientId: ClientId): CoProgress {
  return useSyncExternalStore(subscribe, () => getProgress(clientId));
}

export function useCoAnswer<T>(clientId: ClientId, key: string): T | undefined {
  return useSyncExternalStore(subscribe, () => {
    const entry = entries.get(clientId);
    return entry ? (entry.answers[key] as T | undefined) : undefined;
  });
}

export function useCoLoadState(clientId: ClientId): { loading: boolean; error?: string } {
  return useSyncExternalStore(subscribe, () => {
    const key = `${clientId}|${loading.has(clientId) && !entries.has(clientId)}|${loadErrors.get(clientId) ?? ""}`;
    // Cached so the snapshot is referentially stable between emits.
    if (loadStateCache.key !== key) {
      loadStateCache.key = key;
      loadStateCache.value = {
        loading: loading.has(clientId) && !entries.has(clientId),
        error: loadErrors.get(clientId),
      };
    }
    return loadStateCache.value;
  });
}

const loadStateCache: { key: string; value: { loading: boolean; error?: string } } = {
  key: "",
  value: { loading: false },
};
